import React, {useEffect, useState} from 'react';
import Section from "../../components/section";
import HelperService from "../../services/helper-service";
import MainLayout from "../../layouts/main";
import {useGlobalNotificationMessage} from "../../context/message";
import InitialLoading from "../../components/initial-loading";
import {useMembershipService} from "../../services/membership-service";
import UserAvatar from "../../components/user-avatar";
import {AppEvents, TransactionType} from "../../utils/constants";
import FormatService from "../../services/format-service";
import {useEventsService} from "../../services/events-service";
import {getPersistedDisplayUsers} from "../../utils/auth";
import {useArticleService} from "../../services/article-service";
import Manager from "../../components/buildings/manager";
import {useManagerService} from "../../services/manager-service";
import {Accordion, Modal, Tab, Nav} from "react-bootstrap";
import DataTableBase from "../../components/datatable/data-table-base";
import MyHighchart from "../../components/my-highchart";
import ArticlesBox from "../../components/articles/articles-box";
import Attachment from "../../components/files/attachment";
import GalleryWrapper from "../../components/attachments/gallery-wrapper";

export default function Membership () {
  const membershipService = useMembershipService();
  const eventsService = useEventsService();
  const articleService = useArticleService();
  const managerService = useManagerService();

  const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();
  const [articles, setArticles] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [transactionChartData, setTransactionChartData] = useState(null);
  const [internalValueChartData, setInternalValueChartData] = useState(null);
  const [managers, setManagers] = useState(null);
  const [displayUsers, setDisplayUsers] = useState(getPersistedDisplayUsers());
  const [showTransactionsDataModal, setShowTransactionsDataModal] = useState(false);
  const [showInternalValueDataModal, setShowInternalValueDataModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [documents, setDocuments] = useState([]);

  const dayjs = HelperService.getDayjs();

  const colors = [
    '#737fed',
    '#5ecb9b',
    '#fa5c7c'
  ];

  useEffect(() => {
    eventsService.on(AppEvents.displayUsersChanged, (event) => {
      setDisplayUsers(getPersistedDisplayUsers());
    })
  }, []);

  useEffect(() => {
    reloadMembership();
    reloadMembershipDocuments();
    reloadArticles();
    reloadManagers();
  }, [displayUsers]);

  function reloadArticles(){
    articleService.getPublished()
      .then((rows)=>{
        setArticles(rows);
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa získať dáta.");
      })
  }

  function reloadMembershipDocuments(){
    membershipService.getDocuments()
      .then((rows)=>{
        setDocuments(rows);
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa získať dáta.");
      })
  }

  function reloadMembership(){
    membershipService.getMembership()
      .then((rows)=>{

        let transactionTableData = null;
        let internalValueTableData = null;

        if (rows.ivChart){
          rows.ivChart = rows.ivChart.map((item, index)=>{
            return {
              x: item.x,
              y: item.y,
              date: item.x,
              parsedDate: dayjs(item.x, "DD.MM.YYYY"),
              internalValue: FormatService.formatNumber(item.y),
            };
          });
          setInternalValueChartData([
            {
              id: 'Interná hodnota',
              name: 'Interná hodnota',
              type: 'area',
              color: colors[1],
              data: rows.ivChart.map((i)=> [i.parsedDate.valueOf(), i.y])
            },
          ]);

          internalValueTableData = rows.ivChart.map((item, index)=>{
            return {
              date: item.x,
              parsedDate: item.parsedDate,
              internalValue: FormatService.formatNumber(item.y),
              internalValueVal: item.y,
            };
          });

        } else {
          setInternalValueChartData(null);
          internalValueTableData = null;
        }

        const firstDate = dayjs('2019-05-01');
        if (rows.chart && rows.chart.transactions?.length && rows.chart.accountValue?.length){
          let newMaxYValue = 0;
          rows.chart.transactions = rows.chart.transactions.map((item)=>{
            item.parsedDate = dayjs(item.x, "DD.MM.YYYY");
            return item;
          }).filter((item)=>{
            return item.parsedDate >= firstDate;
          });
          rows.chart.accountValue = rows.chart.accountValue.map((item)=>{
            item.parsedDate = dayjs(item.x, "DD.MM.YYYY");
            return item;
          }).filter((item)=>{
            return item.parsedDate >= firstDate;
          });
          setTransactionChartData([
            {
              id: 'Hodnota účtu',
              name: 'Hodnota účtu',
              type: 'area',
              color: colors[1],
              data: rows.chart.accountValue.map((i)=> [i.parsedDate.valueOf(), i.y])
            },
            {
              id: 'Vklady a výbery',
              name: 'Vklady a výbery',
              type: 'area',
              color: colors[0],
              data: rows.chart.transactions.map((i)=> [i.parsedDate.valueOf(), i.y])
            },
          ]);
          transactionTableData = rows.chart.accountValue.map((item, index)=>{
            return {
              date: item.x,
              parsedDate: item.parsedDate,
              accountValue: FormatService.formatNumber(item.y) + ' €',
              accountValueVal: item.y,
              transactions: FormatService.formatNumber(rows.chart.transactions[index]?.y) + ' €',
              transactionsVal: rows.chart.transactions[index]?.y
            };
          });
        } else {
          setTransactionChartData(null);
          transactionTableData = null;
        }

        rows.group_users = rows.group_users.map((gu)=>{
          gu.transactions = rows.transactions.filter((item)=>item.user_id===gu.id);
          gu.sum_transactions = gu.transactions.reduce((sum, item)=>{return item.type===TransactionType.credit.val ? sum + item.value : sum - item.value }, 0);
          gu.sum_membership_shares = gu.membership_shares.reduce((sum, item)=>{return sum + item.value;}, 0);
          gu.shares_value = rows.lastInternalValue.value * gu.sum_membership_shares;
          gu.profit = gu.shares_value - gu.sum_transactions;
          return gu;
        })

        let transactionsMin = rows?.chart?.transactions?.length ? Math.min(...rows.chart.transactions.map(i=>i.y)) : 0;
        let accountValueMin = rows?.chart?.accountValue?.length ? Math.min(...rows.chart.accountValue.map(i=>i.y)) : 0;


        rows.transactionTableData = transactionTableData;
        rows.internalValueTableData = internalValueTableData;
        rows.transactionChartMin = Math.min(transactionsMin, accountValueMin);

        setMembership(rows);
        setLoaded(true);
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa získať dáta.");
      })
  }

  function reloadManagers(){
    managerService.getMembership()
      .then((rows)=>{
        setManagers(rows);
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa získať dáta.");
      })
  }

  return (
    <>

      {!loaded && <InitialLoading />}

      {loaded && <Section animation={HelperService.getRandomAnimation()}>
        <div className="container mt-4">
          <div className="row">
            <div className="col-sm-12">
              <Section animation={HelperService.getRandomAnimation()}>
                <div className="col-12 mb-4">
                  <div className="card h-100 p-relative mb-0">
                    <div className='row clickable' onClick={()=>setShowInfoModal(true)}>
                      <div className='col-sm-12 col-md-3'>
                        <img src="/images/bg-gradient.jpg" className="w-100 h-100 rounded-start" alt="" />
                      </div>
                      <div className='col-sm-12 col-md-9'>
                        <div className="card-body">
                          <h3 className="my-2">Investičný Holding</h3>
                          <p className="card-text my-2 building-portfolio-text" style={{maxHeight: '110px'}}>
                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut
                            labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                            laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in
                            voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat
                            non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Modal size={"xl"} show={!!showInfoModal} onHide={()=>setShowInfoModal(false)} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>Investičný Holding</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <Section animation={HelperService.getRandomAnimation()} className={'py-3'}>
                      <p>
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in
                        voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat
                        non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                      </p>
                    </Section>
                  </Modal.Body>
                </Modal>
              </Section>

              <Section animation={HelperService.getRandomAnimation()}>
                <div className="row">
                  <div className="col-sm-12 col-md-7 col-lg-9">
                    <h2>Novinky</h2>
                    <ArticlesBox articles={articles} maxHeight='490px' max={3} />
                  </div>
                  <div className="col-sm-12 col-md-5 col-lg-3">
                    <h2>Členovia skupiny</h2>
                    <Accordion defaultActiveKey="0" className={'card custom-accordion'}>
                      {membership?.group_users && membership?.group_users.map((gu, index)=>
                        <Accordion.Item className="card mb-0" eventKey={index.toString()} key={gu.id}>
                          <Accordion.Header className="custom-accordion-title">
                            <div className="d-flex flex-sm-column flex-md-row align-items-center justify-content-between text-sm-center text-md-start gap-2">
                              <UserAvatar width={40} user={gu} />
                              <div className="col-sm-12 col-md-10">
                                <h5 className={"mb-1 mt-0"}>{gu.name}</h5>
                                <h5 className={"mt-1 mb-0 fw-normal"}>Člen družstva</h5>
                              </div>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <table className="table table-sm mb-0">
                              <tbody>
                              <tr>
                                <th className="fw-normal">Dátum vzniku členstva</th>
                                <td className={"fw-bold"}>{gu.membership_start_at ? HelperService.formatDate(gu.membership_start_at) : '-'}</td>
                              </tr>
                              <tr>
                                <th className="fw-normal">Status</th>
                                <td className={"fw-bold"}>{gu.membership_end_at ? 'Členstvo zaniklo ku '+ HelperService.formatDate(gu.membership_end_at): 'Aktívny'}</td>
                              </tr>
                              <tr>
                                <th className="fw-normal">Čistá suma vkladov</th>
                                <td className={"fw-bold"}>{FormatService.formatNumber(gu.sum_transactions)} €</td>
                              </tr>
                              <tr>
                                <th className="fw-normal">Počet členských podielov</th>
                                <td className={"fw-bold"}>{gu.sum_membership_shares}</td>
                              </tr>
                              <tr>
                                <th className="fw-normal">Interná hodnota 1 podielu</th>
                                <td className={"fw-bold"}>{FormatService.formatNumber(membership?.lastInternalValue?.value)} €</td>
                              </tr>
                              <tr style={{'borderColor':'white'}}>
                                <th className="fw-normal">Hodnota členských podielov</th>
                                <td className={"fw-bold"}>{FormatService.formatNumber(gu.shares_value)} €</td>
                              </tr>
                              </tbody>
                            </table>
                            <div className={`d-flex justify-content-between align-items-center bg-${gu.profit>=0?'success':'danger'} px-2 py-1 rounded gap-2 my-1 col-sm-12 text-white`}>
                              <span className="me-2 fw-bold">Zisk</span><strong>{FormatService.formatNumber(gu.profit)} €</strong>
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      )}
                    </Accordion>
                  </div>
                </div>
              </Section>

              {!membership && <div className="col-12">
                <div className="card">
                  <div className="card-body text-center">
                    <p>Zatiaľ prázdne :(</p>
                  </div>
                </div>
              </div>}

              <div className="row">
                {transactionChartData && <div className="col-sm-12 col-md-6">
                  <h2>Vývoj hodnoty účtu</h2>
                  <div className="card">
                    <div className="card-body">
                      <div>
                        <MyHighchart titleY='Hodnota (EUR)' options={{
                          series: transactionChartData,
                          yAxis: {
                            min: membership.transactionChartMin,
                          },
                          chart: {
                            height: 330
                          },
                        }} />
                      </div>
                      <div className={"mt-1 text-center"}>
                        <button type="button" className="btn btn-sm btn-soft-secondary rounded-pill" onClick={()=>setShowTransactionsDataModal(true)}>Podrobnosti <span className="mdi mdi-chevron-right"></span></button>
                      </div>
                      <div className="clearfix"></div>
                    </div>
                  </div>

                  <Modal size="lg" show={!!showTransactionsDataModal} onHide={()=>setShowTransactionsDataModal(false)} centered>
                    <Modal.Header closeButton>
                      <Modal.Title>Vývoj hodnoty účtu</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <Section animation={HelperService.getRandomAnimation()}>
                        {membership.transactionTableData && <DataTableBase
                          columns={[{
                            name: 'Dátum',
                            selector: row => row.date,
                            sortFunction: (a,b)=>HelperService.dateSortFunction(a,b,'parsedDate'),
                            sortable: true,
                          },{
                            name: 'Hodnota účtu',
                            selector: row => row.accountValueVal,
                            format: row => row.accountValue,
                            sortable: true,
                          },{
                            name: 'Vklady a výbery',
                            selector: row => row.transactionsVal,
                            format: row => row.transactions,
                            sortable: true,
                          },]}
                          data={membership.transactionTableData}
                        />}
                      </Section>
                    </Modal.Body>
                  </Modal>

                </div>}

                {internalValueChartData && <div className="col-sm-12 col-md-6">
                  <h2>Vývoj internej hodnoty</h2>
                  <div className="card">
                    <div className="card-body">
                      <div>
                        <MyHighchart titleY='Interná hodnota (EUR)' options={{
                          series: internalValueChartData,
                          yAxis: {
                            min: 0.85,
                          },
                          chart: {
                            height: 330
                          },
                        }} />
                      </div>
                      <div className={"mt-1 text-center"}>
                        <button type="button" className="btn btn-sm btn-soft-secondary rounded-pill" onClick={()=>setShowInternalValueDataModal(true)}>Podrobnosti <span className="mdi mdi-chevron-right"></span></button>
                      </div>
                      <div className="clearfix"></div>
                    </div>
                  </div>

                  <Modal size="lg" show={!!showInternalValueDataModal} onHide={()=>setShowInternalValueDataModal(false)} centered>
                    <Modal.Header closeButton>
                      <Modal.Title>Vývoj hodnoty účtu</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <Section animation={HelperService.getRandomAnimation()}>
                        {membership.internalValueTableData && <DataTableBase
                          columns={[{
                            name: 'Dátum',
                            selector: row => row.date,
                            sortFunction: (a,b)=>HelperService.dateSortFunction(a,b,'parsedDate'),
                            sortable: true,
                          },{
                            name: 'Interná hodnota',
                            selector: row => row.internalValueVal,
                            format: row => row.internalValue + ' €',
                            sortable: true,
                          },]}
                          data={membership.internalValueTableData}
                        />}
                      </Section>
                    </Modal.Body>
                  </Modal>

                </div>}
              </div>


              <Section animation={HelperService.getRandomAnimation()} className="row">
                <div className="col-sm-12 col-md-6">
                  <h2>Dokumenty</h2>
                  <div className="card">
                    <div className="card-body">
                      <Tab.Container defaultActiveKey="my-documents">

                        <Nav className="nav nav-tabs bg-nav-pills nav-justified mb-3" style={{padding: '6px 6px 0', borderRadius: '20px 20px 0 0', borderBottom: 'none'}}>
                          <Nav.Item>
                            <Nav.Link eventKey="my-documents" className="d-flex align-items-center justify-content-center">Moje dokumenty</Nav.Link>
                          </Nav.Item>
                          {documents?.documentCategories?.length > 0 && <>{documents.documentCategories.map((c) => {
                            return <Nav.Item key={'tabkey-' + c.id}>
                              <Nav.Link eventKey={'tab-' + c.id}
                                        className="d-flex align-items-center justify-content-center">{c.title}</Nav.Link>
                            </Nav.Item>;
                          })}</>}
                        </Nav>

                        <Tab.Content style={{maxHeight: '420px', overflow: 'auto'}}>

                          <Tab.Pane eventKey="my-documents">
                            <div className="row w-100">
                              {!documents?.groupDocuments?.length > 0 && <div className="text-muted text-center p-5 col-sm-12">Zatiaľ nemáte nahrané žiadne dokumenty.</div>}
                              {documents?.groupDocuments?.length > 0 && <>{documents.groupDocuments.map((d) => {
                                return <div key={"groupDocuments-"+d.id} className="col-md-12 col-lg-6"><Attachment controls={false} attachment={d}/></div>;
                              })}</>}
                            </div>
                          </Tab.Pane>

                          {documents?.documentCategories?.length > 0 && <>{documents.documentCategories.map((c) => {
                            return <Tab.Pane eventKey={'tab-' + c.id} key={'tabpanekey-' + c.id}>
                              <div className="row w-100">
                                {!c?.attachments?.length > 0 &&
                                  <div className="text-muted text-center p-5 col-sm-12">V tejto kategórii zatiaľ nie sú
                                    nahrané žiadne dokumenty.</div>}
                                {c?.attachments?.length > 0 && <>{c.attachments.map((d) => {
                                  return <div key={"documentCategoriesDocuments-"+d.id} className="col-md-12 col-lg-6"><Attachment controls={false} attachment={d}/></div>;
                                })}</>}
                              </div>
                            </Tab.Pane>
                              ;
                          })}</>}

                        </Tab.Content>

                      </Tab.Container>
                    </div>
                  </div>
                </div>

                <div className="col-sm-12 col-md-6">
                  <h2>Galéria</h2>
                  <div className="card">
                    <div className="card-body">
                      <Tab.Container defaultActiveKey={documents?.galleryCategories?.length ? 'gal-'+documents.galleryCategories[0].id : "" }>

                        <Nav className="nav nav-tabs bg-nav-pills nav-justified mb-3"
                             style={{padding: '6px 6px 0', borderRadius: '20px 20px 0 0', borderBottom: 'none'}}>
                          {documents?.galleryCategories?.length > 0 && <>{documents.galleryCategories.map((c) => {
                            return <Nav.Item key={'galkey-' + c.id}>
                              <Nav.Link eventKey={'gal-' + c.id}
                                        className="d-flex align-items-center justify-content-center">{c.title}</Nav.Link>
                            </Nav.Item>;
                          })}</>}
                        </Nav>

                        <Tab.Content style={{maxHeight: '420px', overflow: 'auto'}}>

                          {documents?.galleryCategories?.length > 0 && <>{documents.galleryCategories.map((c) => {
                            return <Tab.Pane eventKey={'gal-' + c.id}  key={'galpanekey-' + c.id}>
                              <div className="row w-100">

                                {!c?.attachments?.length > 0 &&
                                  <div className="text-muted text-center p-5 col-sm-12">V tejto kategórii zatiaľ nie sú
                                    nahrané žiadne obrázky.</div>}
                                {c?.attachments?.length > 0 && <>
                                  <GalleryWrapper elementClassNames={"d-flex gap-2"}>
                                    {c.attachments.map((d) => {
                                      return <a key={'gwi_' + d.id} href={d.url} className="d-inline-block card p-1 overflow-hidden" style={{maxWidth: '100px', maxHeight: '100px'}}>
                                            <img src={d.thumbUrl} alt={d.title}
                                                 style={{maxWidth: '100px', maxHeight: '100%', objectFit: 'cover'}}/>
                                      </a>;
                                    })}
                                  </GalleryWrapper>
                                </>}

                              </div>
                            </Tab.Pane>
                              ;
                          })}</>}

                        </Tab.Content>

                      </Tab.Container>
                    </div>
                  </div>
                </div>
              </Section>

              {membership && <div animation={HelperService.getRandomAnimation()} className="row">

                {transactionChartData && <div className="col-sm-12 col-md-12">
                  <h2>Transakcie</h2>
                  <div className="card">
                    <div className="card-body">

                      {!membership?.transactions?.length > 0 &&
                        <div className="text-muted">Zatiaľ neevidujeme žiadne vklady ani výbery.</div>}
                      {membership?.transactions?.length > 0 && <>
                        <div className={'table-responsive'}>
                          <table className="table table-sm" style={{minWidth: '500px'}}>
                            <thead>
                            <tr>
                              <th>Dátum</th>
                              <th>Používateľ</th>
                              <th>Hodnota</th>
                              <th>Interná hodnota</th>
                              <th>Členský podiel</th>
                            </tr>
                            </thead>

                            <tbody>
                            {membership.transactions.map((t) => {
                              return <tr key={t.id}>
                                <td>
                                  {t.type === TransactionType.credit.val ?
                                    <span className="mdi mdi-arrow-up-bold text-success me-2"></span> :
                                    <span className="mdi mdi-arrow-down-bold text-danger me-2"></span>}
                                  {HelperService.formatDate(t.value_at)}
                                </td>
                                <td>{t.user.name}</td>
                                <td>{t.type === TransactionType.credit.val ?
                                  <span>{FormatService.formatNumber(t.value)} €</span> :
                                  <span className="text-danger">-{FormatService.formatNumber(t.value)} €</span>}</td>
                                <td>{FormatService.formatNumber(t.membership_share?.internal_value?.value)} €</td>
                                <td>{t.membership_share?.value}</td>
                              </tr>;
                            })}
                            </tbody>
                          </table>
                        </div>

                        <div
                          className={'d-flex justify-content-between align-content-center gap-2 flex-wrap flex-column flex-sm-row align-content-sm-start justify-content-sm-start'}>
                          <span
                            className={'me-2 p-1 px-2 rounded bg-light'}>Vklady: <strong>{FormatService.formatNumber(membership.transactions.reduce((sum, item) => {
                            return item.type === TransactionType.credit.val ? sum + item.value : sum
                          }, 0))} €</strong></span>
                          <span className={'me-2 p-1 px-2 rounded bg-light'}>Výbery: <strong>{FormatService.formatNumber(membership.transactions.reduce((sum, item)=>{return item.type===TransactionType.debet.val ? sum + item.value : sum },0))} €</strong></span>
                          <span className={'me-2 p-1 px-2 rounded bg-indigo  text-white'}>Čistá hodnota vkladov: <strong>{FormatService.formatNumber(membership.transactions.reduce((sum, item)=>{return item.type===TransactionType.credit.val ? sum + item.value : sum - item.value },0))} €</strong></span>
                        </div></>}

                    </div>
                  </div>

                </div>}

              </div>}

              <Section animation={HelperService.getRandomAnimation()}>
                <h2>Správcovia</h2>
                <div className="col-12 mb-4">
                  <div className="row">
                    {!managers?.length>0 && <div className="card">
                      <div className="card-body">
                        <p className="text-muted">Zatiaľ prázdne :)</p>
                      </div>
                    </div>}
                    {managers?.length>0 && managers.map((man, i)=>{
                      return <div key={man.id} className={`col-sm-12 col-md-6 col-lg-4`}><Manager manager={man} className={i===1 ? 'bg-success text-white' : null} /></div>;
                    })}
                  </div>
                </div>
              </Section>

            </div>
          </div>
        </div>
      </Section>}
    </>
  );
}

Membership.getLayout = function getLayout(page) {
  return (
    <MainLayout>
      {page}
    </MainLayout>
  )
}

Membership.getSEO = function getSEO() {
  return {
    title: "Družstvo",
  }
}
